import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency } from '@/lib/utils';
import type { LeadConversionMetrics, OverviewAnalyticsData } from '@/types/analytics';
import { Briefcase, CalendarClock, ClipboardList, DollarSign, FileSignature, Target } from 'lucide-react';
import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

interface Props {
    leadMetrics?: LeadConversionMetrics;
    overviewAnalytics?: OverviewAnalyticsData;
}

export default function OverviewAnalytics({ leadMetrics, overviewAnalytics }: Props) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const revenueTrendHeightClass = isMobile ? 'h-[240px]' : 'h-[320px]';
    const trendHeightClass = useChartHeightClass('md');

    const kpis = useMemo(() => {
        const totalRevenue = Number(overviewAnalytics?.kpis?.total_revenue_period ?? 0);
        const prevRevenue = Number(overviewAnalytics?.kpis?.total_revenue_prev_period ?? 0);
        // Period-over-period change for the revenue KPI; null when there's no prior baseline to compare against.
        const revenueDelta = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : null;
        const winRate = Number(overviewAnalytics?.kpis?.win_rate ?? leadMetrics?.conversion_rate ?? 0);
        const activePipeline = Number(overviewAnalytics?.kpis?.active_pipeline_value ?? 0);
        const activeWorkOrders = Number(overviewAnalytics?.kpis?.active_work_orders ?? 0);
        const projectsSignedValue = Number(overviewAnalytics?.kpis?.projects_signed_value ?? 0);
        const projectsSignedCount = Number(overviewAnalytics?.kpis?.projects_signed_count ?? 0);
        return { totalRevenue, revenueDelta, winRate, activePipeline, activeWorkOrders, projectsSignedValue, projectsSignedCount };
    }, [overviewAnalytics, leadMetrics]);

    const leadsProjectsTimeline = overviewAnalytics?.leads_projects_timeline ?? [];
    const revenueBySource = overviewAnalytics?.revenue_by_source_timeline ?? [];
    const activityTimeline = overviewAnalytics?.activity_timeline ?? [];
    const workOrderTrend = overviewAnalytics?.work_order_trend ?? [];

    const hasRevenue = revenueBySource.some((r) => (r.projects ?? 0) > 0 || (r.leads ?? 0) > 0);
    const hasActivity = activityTimeline.some((r) => (r.meetings ?? 0) > 0 || (r.calls ?? 0) > 0);
    const hasWorkOrders = workOrderTrend.some((r) => (r.created ?? 0) > 0 || (r.completed ?? 0) > 0);

    const revenueConfig = {
        projects: { label: t('analytics.labels.revenue_src_projects', 'Projects'), color: '#2563eb' },
        leads: { label: t('analytics.labels.revenue_src_leads', 'Leads'), color: '#f59e0b' },
    } satisfies Record<string, { label: string; color: string }>;

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold">{t('analytics.labels.page_1_company_overview_dashboard', 'Company Overview Dashboard')}</h2>
                <p className="text-muted-foreground text-sm">
                    {t('analytics.labels.executive_snapshot', 'Executive snapshot of revenue, pipeline, delivery and service operations.')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <SummaryMetricCard
                    docKey="overview.total_revenue"
                    title={t('analytics.labels.total_revenue', 'Total Revenue')}
                    value={formatCurrency(kpis.totalRevenue)}
                    subtitle={t('analytics.labels.selected_period', 'Selected period')}
                    icon={<DollarSign className="h-4 w-4" />}
                    delta={kpis.revenueDelta ?? undefined}
                    deltaLabel={kpis.revenueDelta !== null ? t('analytics.labels.vs_previous_period', 'vs previous period') : undefined}
                />
                <SummaryMetricCard
                    docKey="overview.win_rate"
                    title={t('analytics.labels.win_rate', 'Win Rate')}
                    value={`${kpis.winRate.toFixed(1)}%`}
                    subtitle={t('analytics.labels.lead_to_won_performance', 'Lead to won performance')}
                    icon={<Target className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="overview.active_pipeline"
                    title={t('analytics.labels.active_pipeline', 'Active Pipeline')}
                    value={formatCurrency(kpis.activePipeline)}
                    subtitle={t('analytics.labels.active_projects_value', 'Active projects value')}
                    icon={<Briefcase className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="overview.active_work_orders"
                    title={t('analytics.labels.active_work_orders', 'Active Work Orders')}
                    value={kpis.activeWorkOrders}
                    subtitle={t('analytics.labels.current_service_load', 'Current service load')}
                    icon={<ClipboardList className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="overview.projects_signed"
                    title={t('analytics.labels.projects_signed_value', 'Projects Signed')}
                    value={formatCurrency(kpis.projectsSignedValue)}
                    subtitle={`${kpis.projectsSignedCount} ${t('analytics.labels.signed_in_period', 'signed in period')}`}
                    icon={<FileSignature className="h-4 w-4" />}
                />
            </div>

            {/* What we closed: revenue by source */}
            <AnalyticsChartCard
                docKey="overview.revenue_by_source"
                title={t('analytics.labels.revenue_closed_title', 'Revenue Trend (closed)')}
                description={t(
                    'analytics.labels.revenue_closed_desc',
                    'Value closed in the selected period by source — won leads and completed projects.',
                )}
            >
                {hasRevenue ? (
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.revenue_closed_title', 'Revenue Trend (closed)')}
                        config={revenueConfig}
                        className={`${revenueTrendHeightClass} w-full`}
                    >
                        <AreaChart data={revenueBySource}>
                            <defs>
                                <linearGradient id="fillRevProjects" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--color-projects)" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="var(--color-projects)" stopOpacity={0.05} />
                                </linearGradient>
                                <linearGradient id="fillRevLeads" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="var(--color-leads)" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="var(--color-leads)" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={(v) => formatCurrency(Number(v))} tick={{ fontSize: 11 }} width={isMobile ? 56 : 90} />
                            <ChartTooltip content={<ChartTooltipContent formatter={(v) => formatCurrency(Number(v))} />} />
                            <ChartLegend content={<ChartLegendContent />} />
                            <Area dataKey="leads" type="monotone" stroke="var(--color-leads)" strokeWidth={2} fill="url(#fillRevLeads)" />
                            <Area dataKey="projects" type="monotone" stroke="var(--color-projects)" strokeWidth={2} fill="url(#fillRevProjects)" />
                        </AreaChart>
                    </ChartContainer>
                ) : (
                    <div className={`text-muted-foreground flex ${revenueTrendHeightClass} items-center justify-center text-sm`}>
                        {t('analytics.labels.no_revenue_in_period', 'No revenue in this period')}
                    </div>
                )}
            </AnalyticsChartCard>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* New leads & projects (inflow) */}
                <AnalyticsChartCard
                    docKey="overview.leads_projects_trend"
                    title={t('analytics.labels.leads_and_projects_trend', 'Leads & Projects Trend')}
                    description={t('analytics.labels.leads_projects_trend_desc', 'New leads and started projects over the selected period.')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.leads_and_projects_trend', 'Leads & Projects Trend')}
                        config={{
                            leads: { label: t('analytics.labels.new_leads_series', 'New leads'), color: '#2563eb' },
                            projects: { label: t('analytics.labels.new_projects_series', 'New projects'), color: '#7c3aed' },
                        }}
                        className={`${trendHeightClass} w-full`}
                    >
                        <AreaChart data={leadsProjectsTimeline}>
                            <defs>
                                <linearGradient id="fillLeads" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                                </linearGradient>
                                <linearGradient id="fillProjects" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.4} />
                                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <ChartLegend content={<ChartLegendContent />} />
                            <Area dataKey="leads" type="monotone" stroke="#2563eb" strokeWidth={2} fill="url(#fillLeads)" />
                            <Area dataKey="projects" type="monotone" stroke="#7c3aed" strokeWidth={2} fill="url(#fillProjects)" />
                        </AreaChart>
                    </ChartContainer>
                </AnalyticsChartCard>

                {/* Work order trend: created vs completed */}
                <AnalyticsChartCard
                    docKey="overview.work_order_trend"
                    title={t('analytics.labels.wo_trend_title', 'Work Order Trend')}
                    description={t('analytics.labels.wo_trend_desc', 'Work orders created vs completed in the selected period.')}
                >
                    {hasWorkOrders ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.wo_trend_title', 'Work Order Trend')}
                            config={{
                                created: { label: t('analytics.labels.created', 'Created'), color: '#2563eb' },
                                completed: { label: t('analytics.labels.completed', 'Completed'), color: '#16a34a' },
                            }}
                            className={`${trendHeightClass} w-full`}
                        >
                            <AreaChart data={workOrderTrend}>
                                <defs>
                                    <linearGradient id="fillWoCreated" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="fillWoCompleted" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <ChartLegend content={<ChartLegendContent />} />
                                <Area dataKey="created" type="monotone" stroke="#2563eb" strokeWidth={2} fill="url(#fillWoCreated)" />
                                <Area dataKey="completed" type="monotone" stroke="#16a34a" strokeWidth={2} fill="url(#fillWoCompleted)" />
                            </AreaChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${trendHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_work_orders_found', 'No work orders found')}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>

            {/* Activity: meetings & phone calls */}
            <AnalyticsChartCard
                docKey="overview.activity_trend"
                title={t('analytics.labels.activity_trend', 'Activity Trend')}
                description={t('analytics.labels.activity_trend_desc', 'Meetings and phone calls logged in the selected period.')}
            >
                {hasActivity ? (
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.activity_trend', 'Activity Trend')}
                        config={{
                            meetings: { label: t('analytics.labels.meetings', 'Meetings'), color: '#7c3aed' },
                            calls: { label: t('analytics.labels.phone_calls', 'Phone calls'), color: '#0891b2' },
                        }}
                        className={`${trendHeightClass} w-full`}
                    >
                        <LineChart data={activityTimeline}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <ChartLegend content={<ChartLegendContent />} />
                            <Line dataKey="meetings" type="monotone" stroke="#7c3aed" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                            <Line dataKey="calls" type="monotone" stroke="#0891b2" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                        </LineChart>
                    </ChartContainer>
                ) : (
                    <div className={`text-muted-foreground flex ${trendHeightClass} items-center justify-center gap-2 text-sm`}>
                        <CalendarClock className="h-4 w-4" />
                        {t('analytics.labels.no_activity_logged', 'No meetings or phone calls logged in this period')}
                    </div>
                )}
            </AnalyticsChartCard>
        </div>
    );
}
